import { Children, createContext, createRef, useContext, useRef, useState } from 'react';
import { useFormState } from 'react-hook-form';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@carbon/react';
import _ from 'lodash';

import DiscardChangesConfirmationModal from 'features/inputs/components/DiscardChangesConfirmationModal';
import useNavigationBlocker from 'utils/hooks/useNavigationBlocker';
import useUnmount from 'utils/hooks/useUnmount';
import styles from 'theme/features/inputs/inputs-form.module.scss';

const projectFormTabDirtyContext = createContext(createRef());
const setOtherPageNavigationTransitionContext = createContext(_.noop);
const tabIndexContext = createContext(0);

const useIsDirty = (control) => {
  const controls = Array.isArray(control) ? control : [control];

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isDirty = controls.map((c) => useFormState({ control: c }).isDirty);

  return isDirty.includes(true);
};

export const useTrackProjectFormTabDirtyStatus = (control) => {
  const tabDirtyMap = useContext(projectFormTabDirtyContext);
  const setNavigationTransition = useContext(setOtherPageNavigationTransitionContext);
  const tabIndex = useContext(tabIndexContext);

  const isDirty = useIsDirty(control);

  tabDirtyMap.current[tabIndex] = isDirty;

  useNavigationBlocker(setNavigationTransition, isDirty);

  useUnmount(() => {
    tabDirtyMap[tabIndex] = false;
  });

  const handleTryAction =
    (action) =>
    (...args) => {
      if (isDirty) {
        setNavigationTransition({
          retry: () => action(...args)
        });
      } else {
        action(...args);
      }
    };

  return {
    isDirty,
    handleTryAction
  };
};

const ProjectFormNavigation = ({ children, headers, selectedIndex, onIndexChange, isEditView, isLoadingProject }) => {
  const [requestedTabIndex, setRequestedTabIndex] = useState(null);
  const [otherPageNavigationTransition, setOtherPageNavigationTransition] = useState(null);

  const tabDirtyMapRef = useRef({});

  const handleSelectedTabChange = ({ selectedIndex: newSelectedIndex }) => {
    if (tabDirtyMapRef.current[selectedIndex]) {
      setRequestedTabIndex(newSelectedIndex);
    } else {
      onIndexChange(newSelectedIndex);
    }
  };

  const handleTabTransitionDiscardChanges = () => {
    onIndexChange(requestedTabIndex);
    setRequestedTabIndex(null);
  };

  const shouldDisableOtherTabs = isLoadingProject || !isEditView;

  return (
    <>
      <DiscardChangesConfirmationModal
        open={requestedTabIndex !== null}
        onCancel={() => setRequestedTabIndex(null)}
        onDiscard={handleTabTransitionDiscardChanges}
      />
      <DiscardChangesConfirmationModal
        open={otherPageNavigationTransition !== null}
        onCancel={() => setOtherPageNavigationTransition(null)}
        onDiscard={() => {
          setOtherPageNavigationTransition(null);
          otherPageNavigationTransition.retry();
        }}
      />
      <projectFormTabDirtyContext.Provider value={tabDirtyMapRef}>
        <setOtherPageNavigationTransitionContext.Provider value={setOtherPageNavigationTransition}>
          <Tabs selectedIndex={selectedIndex} onChange={handleSelectedTabChange}>
            <TabList className={styles['tab-list']} aria-label="Create project form">
              {headers.map((x, i) => (
                <Tab key={x} disabled={shouldDisableOtherTabs && i !== 0}>
                  {x}
                </Tab>
              ))}
            </TabList>
            <TabPanels>
              {Children.map(children, (p, i) => (
                <TabPanel key={i} className={styles['tab-panel']}>
                  <tabIndexContext.Provider value={i}>{i === selectedIndex && p}</tabIndexContext.Provider>
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </setOtherPageNavigationTransitionContext.Provider>
      </projectFormTabDirtyContext.Provider>
    </>
  );
};

export default ProjectFormNavigation;
